import * as React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import DiscussionForm, { MAX_DESCRIPTION_LENGTH } from "./DiscussionForm";
import TagSelector from "./TagSelector";
import StatusItemSelector from "./StatusItemSelector";
import {
  DiscussionDto,
  updateDiscussion,
  UpdateDiscussionRequest,
} from "../../../services/discussions.service";
import { getStatusItemsBySpreadsheet, StatusItemDto } from "../../../services/status-items.service";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { getTagsForMainType, findTypeById } from "../types/discussionTypes";
import { RichTextValue, MentionUser } from "./richtext/types";
import { extractUserEmails } from "./richtext/parser";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    minWidth: "400px",
  },
  typeHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  typeIcon: {
    width: "24px",
    height: "24px",
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalS,
    width: "100%",
  },
});

interface EditDiscussionDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  discussion: DiscussionDto;
  users?: MentionUser[];
  onSuccess: (updatedDiscussion: DiscussionDto) => void;
}

/**
 * Modal for editing an existing discussion's title, description, tags,
 * mentions, and linked status items. Only the original author can edit.
 */
const EditDiscussionDialog: React.FC<EditDiscussionDialogProps> = ({
  isOpen,
  onDismiss,
  discussion,
  users,
  onSuccess,
}) => {
  const styles = useStyles();

  // State - initialized from discussion
  const [title, setTitle] = useState(discussion.title);
  const [description, setDescription] = useState<RichTextValue>({
    markup: discussion.descriptionContent || "",
    plainText: discussion.description || "",
  });
  const [selectedTags, setSelectedTags] = useState<string[]>(discussion.tags || []);
  const [selectedStatusItemIds, setSelectedStatusItemIds] = useState<number[]>(discussion.statusItemIds || []);
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [statusItems, setStatusItems] = useState<StatusItemDto[]>([]);

  // Reset form when discussion changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      setTitle(discussion.title);
      setDescription({
        markup: discussion.descriptionContent || "",
        plainText: discussion.description || "",
      });
      setSelectedTags(discussion.tags || []);
      setSelectedStatusItemIds(discussion.statusItemIds || []);
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, discussion]);

  // Fetch status items and sheets when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        const items = await getStatusItemsBySpreadsheet(discussion.spreadsheetId);
        setStatusItems(items);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setStatusItems([]);
      }
    };

    fetchData();
  }, [isOpen, discussion.spreadsheetId]);

  // Get type details for display
  const typeDetails = findTypeById(discussion.type);
  const availableTags = getTagsForMainType(discussion.type);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { title?: string; description?: string } = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    } else if (title.length > 100) {
      newErrors.title = "Title must be 100 characters or less";
    }

    if (!description.plainText.trim()) {
      newErrors.description = "Description is required";
    } else if (description.markup.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const request: UpdateDiscussionRequest = {
        title: title.trim(),
        description: description.plainText.trim(),
        descriptionContent: description.markup,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        taggedUserEmails: extractUserEmails(description.markup),
        statusItemIds:
          discussion.type === "review_point" && selectedStatusItemIds.length > 0
            ? selectedStatusItemIds
            : undefined,
      };

      const updatedDiscussion = await updateDiscussion(discussion.id, request);
      onSuccess(updatedDiscussion);
      handleClose();
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    onDismiss();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            Edit Discussion
          </DialogTitle>

          <DialogContent>
            {submitError && (
              <div className={styles.errorMessage}>
                <Text weight="semibold">Error updating discussion</Text>
                <br />
                <Text>{submitError}</Text>
              </div>
            )}

            {/* Type display (read-only) */}
            <div className={styles.typeHeader}>
              <div
                className={styles.typeIcon}
                style={{ color: typeDetails?.color || "#0078D4" }}
              >
                {typeDetails && React.createElement(typeDetails.icon)}
              </div>
              <Badge appearance="filled" color="informative">
                {typeDetails?.name || discussion.type}
              </Badge>
            </div>

            <DiscussionForm
              title={title}
              description={description}
              onTitleChange={setTitle}
              onDescriptionChange={setDescription}
              users={users}
              statusItems={statusItems}
              errors={errors}
            >
              {/* Status item selection for Review Points only */}
              {discussion.type === "review_point" && statusItems.length > 0 && (
                <StatusItemSelector
                  statusItems={statusItems}
                  selectedStatusItemIds={selectedStatusItemIds}
                  onStatusItemIdsChange={setSelectedStatusItemIds}
                />
              )}
            </DiscussionForm>

            {/* Tag selection for types that support tags */}
            {availableTags && availableTags.length > 0 && (
              <TagSelector
                title="Tags"
                tags={availableTags}
                selectedTags={selectedTags}
                onTagsChange={setSelectedTags}
              />
            )}
          </DialogContent>

          <DialogActions>
            <div className={styles.dialogActions}>
              <Button appearance="secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EditDiscussionDialog;
